/**
 * Static config for the Agents Activity → Packages & Repos screen (KIR-3483).
 */

export const AGENT_COMMAND_TABLE_IDS = {
  packages: 'agent-command-events-packages',
  repositories: 'agent-command-events-repositories',
} as const;

export const AGENT_COMMAND_PAGE_SIZE = 25;

/**
 * Packages → Version explanation (KIR-5078), shown on the column header and
 * next to the version in the package dialog. Always shown: a row carries no
 * marker of whether its version was written or resolved.
 */
export const PACKAGE_VERSION_TOOLTIP =
  "If the command named a version, that version is shown. Otherwise Kirin shows the latest public release at the time the command ran, which may differ from what was already installed. — means it couldn't be determined.";

/** Hover text on an empty Branch cell (KIR-4848). */
export const NO_BRANCH_TOOLTIP =
  "The git command the agent ran didn't include a branch, so git used the repository's default or current branch.";

/** Time-range dropdown options. `days: null` = all time (no `from` bound). */
export const TIME_RANGE_OPTIONS = [
  { value: '1', label: 'Last 24 hours', days: 1 },
  { value: '7', label: 'Last 7 days', days: 7 },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: 'all', label: 'All time', days: null },
] as const;

export type TimeRangeValue = (typeof TIME_RANGE_OPTIONS)[number]['value'];

export const DEFAULT_TIME_RANGE: TimeRangeValue = '7';

/**
 * Map a stored agent name to a platform-registry id for the icon/label. The
 * endpoint agent forwards `claude_code` (its PlatformName), but the registry key
 * is `claude` (displayName "Claude Code"), so normalize that one; other names
 * already match registry ids (`cursor`, `copilot`, `gemini_cli`, …) or fall back
 * to a generic icon via getPlatformIcon. Lives here (not CellRenderers) so that
 * component file only exports components (react-refresh/only-export-components).
 */
const AGENT_NAME_TO_PLATFORM_ID: Record<string, string> = {
  claude_code: 'claude',
};

export function agentPlatformId(agentName: string): string {
  const normalized = agentName.toLowerCase();
  return AGENT_NAME_TO_PLATFORM_ID[normalized] ?? normalized;
}

/**
 * ISO `from` bound for a time-range value, or `undefined` for "all time".
 * Pure so it's unit-testable without a clock injection.
 */
export function timeRangeToFromIso(
  value: TimeRangeValue,
  now: Date = new Date()
): string | undefined {
  const option = TIME_RANGE_OPTIONS.find(o => o.value === value);
  if (!option || option.days === null) return undefined;
  return new Date(
    now.getTime() - option.days * 24 * 60 * 60 * 1000
  ).toISOString();
}
